(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const body = document.body;
  const strings = window.themeStrings || {};
  const hiddenVendors = new Set(['My Store', 'Mr Fragrance Hub', 'Perfume Hub', 'Essence Elixir', 'Prime hub Mart']);
  const overlay = qs('[data-mobile-overlay]');
  const toast = qs('[data-toast]');
  let toastTimer;

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.storeCurrency || 'USD' }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
  };

  const closeDrawers = () => {
    qs('[data-mobile-menu]')?.classList.remove('is-open');
    qs('[data-filter-drawer]')?.classList.remove('is-open');
    overlay?.classList.remove('is-visible');
    body.classList.remove('menu-open', 'filter-open');
    qs('[data-mobile-menu]')?.setAttribute('aria-hidden', 'true');
    qs('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  const mobileMenu = qs('[data-mobile-menu]');
  if (mobileMenu && mobileMenu.parentElement !== body) body.appendChild(mobileMenu);
  qs('[data-menu-toggle]')?.addEventListener('click', () => {
    const opening = !mobileMenu?.classList.contains('is-open');
    closeDrawers();
    if (opening && mobileMenu) {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      overlay?.classList.add('is-visible');
      body.classList.add('menu-open');
      qs('[data-menu-toggle]')?.setAttribute('aria-expanded', 'true');
    }
  });
  qs('[data-menu-close]')?.addEventListener('click', closeDrawers);
  overlay?.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawers(); });

  const headerSearch = qs('.header-search');
  qs('[data-search-toggle]')?.addEventListener('click', () => {
    headerSearch?.classList.toggle('is-open');
    if (headerSearch?.classList.contains('is-open')) qs('[data-predictive-input]', headerSearch)?.focus();
  });

  qsa('[data-predictive-search]').forEach((container) => {
    const input = qs('[data-predictive-input]', container);
    const results = qs('[data-predictive-results]', container);
    if (!input || !results) return;
    let timer;
    let controller;

    const clear = () => { results.hidden = true; results.innerHTML = ''; };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { clear(); return; }
      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${window.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Search request failed');
          const data = await response.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = `<div class="predictive-results__footer">${escapeHtml(strings.noMatching)}</div>`;
          } else {
            results.innerHTML = products.map((product) => `
              <a class="predictive-results__item" href="${escapeHtml(product.url)}">
                ${product.image ? `<img src="${escapeHtml(product.image)}" width="62" height="74" alt="">` : '<span></span>'}
                <span><small>${escapeHtml(hiddenVendors.has(product.vendor) ? '' : product.vendor || '')}</small><strong>${escapeHtml(product.title)}</strong></span>
                <strong>${escapeHtml(product.price || '')}</strong>
              </a>`).join('') + `<a class="predictive-results__footer" href="/search?q=${encodeURIComponent(query)}&type=product">${escapeHtml(strings.viewResults)} →</a>`;
          }
          results.hidden = false;
        } catch (error) {
          if (error.name !== 'AbortError') clear();
        }
      }, 220);
    });
    input.addEventListener('keydown', (event) => { if (event.key === 'Escape') clear(); });
    document.addEventListener('click', (event) => { if (!container.contains(event.target)) clear(); });
  });

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      qsa('.cart-count').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (error) {  }
  };

  document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.quick-add-form');
      if (!form) return;
      event.preventDefault();
      const button = qs('button[type="submit"]', form);
      const original = button?.textContent;
      if (button) { button.disabled = true; button.textContent = strings.adding; }
      try {
        const response = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || strings.unableToAdd);
        await updateCartCount();
        showToast(strings.addedToCart);
      } catch (error) {
        showToast(error.message || strings.unableToAdd);
      } finally {
        if (button) { button.disabled = false; button.textContent = original; }
      }
  });

  const wishlistKey = 'joma-shop-destination-wishlist';
  let wishlist = [];
  try { wishlist = JSON.parse(localStorage.getItem(wishlistKey) || '[]'); } catch (error) { wishlist = []; }
  const initializeWishlist = (context = document) => qsa('[data-product-card]', context).forEach((card) => {
    const button = qs('[data-wishlist-button]', card);
    button?.classList.toggle('is-active', wishlist.includes(card.dataset.productHandle));
    button?.setAttribute('aria-pressed', String(wishlist.includes(card.dataset.productHandle)));
  });
  initializeWishlist();
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-button]');
    if (!button) return;
    const handle = button.closest('[data-product-card]')?.dataset.productHandle;
    if (!handle) return;
    wishlist = wishlist.includes(handle) ? wishlist.filter((item) => item !== handle) : [...wishlist, handle];
    try { localStorage.setItem(wishlistKey, JSON.stringify(wishlist)); } catch (error) {}
    initializeWishlist();
    showToast(wishlist.includes(handle) ? strings.savedWishlist : strings.removedWishlist);
  });

  qsa('[data-carousel-section]').forEach((section) => {
    const track = qs('[data-carousel-track]', section);
    if (!track) return;
    const scroll = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.82, behavior: 'smooth' });
    qs('[data-carousel-prev]', section)?.addEventListener('click', () => scroll(-1));
    qs('[data-carousel-next]', section)?.addEventListener('click', () => scroll(1));
  });

  const filterDrawer = qs('[data-filter-drawer]');
  qs('[data-filter-toggle]')?.addEventListener('click', () => {
    filterDrawer?.classList.add('is-open'); overlay?.classList.add('is-visible'); body.classList.add('filter-open');
  });
  qs('[data-filter-close]')?.addEventListener('click', closeDrawers);
  qs('[data-sort-select]')?.addEventListener('change', (event) => { const url = new URL(window.location.href); url.searchParams.set('sort_by', event.target.value); url.searchParams.delete('page'); window.location.assign(url.toString()); });

  const productRoot = qs('[data-product-root]');
  if (productRoot) {
    const thumbs = qsa('[data-gallery-thumb]', productRoot);
    const mediaPanels = qsa('[data-gallery-media]', productRoot);
    const zoomButton = qs('[data-product-zoom]', productRoot);
    const zoomDialog = qs('[data-product-zoom-dialog]', productRoot);
    const zoomImage = qs('[data-zoom-image]', productRoot);
    const syncZoomButton = () => {
      const active = mediaPanels.find((media) => !media.hidden);
      if (zoomButton) zoomButton.hidden = !active?.dataset.zoomSrc || typeof zoomDialog?.showModal !== 'function';
    };
    const activateMedia = (mediaId) => {
      const selectedMedia = mediaPanels.find((media) => media.dataset.mediaId === String(mediaId));
      if (!selectedMedia) return;
      mediaPanels.forEach((media) => {
        const active = media === selectedMedia;
        if (!active && !media.hidden) {
          qsa('video', media).forEach((video) => video.pause());
          qsa('model-viewer', media).forEach((model) => model.pause?.());
          qsa('iframe', media).forEach((frame) => {
            try {
              const url = new URL(frame.src);
              if (/^(www\.)?youtube(-nocookie)?\.com$/.test(url.hostname)) frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), url.origin);
              if (url.hostname === 'player.vimeo.com') frame.contentWindow?.postMessage({ method: 'pause' }, url.origin);
            } catch (error) {}
          });
        }
        media.hidden = !active;
        media.classList.toggle('is-active', active);
      });
      thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
      syncZoomButton();
    };
    thumbs.forEach((thumb) => thumb.addEventListener('click', () => activateMedia(thumb.dataset.mediaId)));
    zoomButton?.addEventListener('click', () => {
      const active = mediaPanels.find((media) => !media.hidden);
      if (!active?.dataset.zoomSrc || !zoomImage || typeof zoomDialog?.showModal !== 'function') return;
      zoomImage.src = active.dataset.zoomSrc;
      zoomImage.alt = active.dataset.zoomAlt || '';
      zoomImage.width = Number(active.dataset.zoomWidth);
      zoomImage.height = Number(active.dataset.zoomHeight);
      zoomDialog.showModal();
    });
    qs('[data-zoom-close]', productRoot)?.addEventListener('click', () => zoomDialog?.close());
    zoomDialog?.addEventListener('click', (event) => { if (event.target === zoomDialog) zoomDialog.close(); });
    syncZoomButton();

    const quantityInput = qs('[data-qty-input]', productRoot);
    const quantityMinus = qs('[data-qty-minus]', productRoot);
    const quantityPlus = qs('[data-qty-plus]', productRoot);
    const quantityBounds = () => {
      const min = Math.max(1, Number(quantityInput?.min) || 1);
      const step = Math.max(1, Number(quantityInput?.step) || 1);
      const max = quantityInput?.max === '' ? Infinity : Number(quantityInput?.max);
      return { min, step, max: Number.isFinite(max) ? min + Math.floor((max - min) / step) * step : Infinity };
    };
    const syncQuantityButtons = () => {
      if (!quantityInput) return;
      const { min, max } = quantityBounds();
      const value = Number(quantityInput.value);
      if (quantityMinus) quantityMinus.disabled = quantityInput.disabled || value <= min;
      if (quantityPlus) quantityPlus.disabled = quantityInput.disabled || value >= max;
    };
    const normalizeQuantity = () => {
      if (!quantityInput) return;
      const { min, max, step } = quantityBounds();
      const entered = Number(quantityInput.value);
      const value = Number.isFinite(entered) ? Math.floor(entered) : min;
      const aligned = min + Math.floor((Math.max(min, value) - min) / step) * step;
      quantityInput.value = String(Math.max(min, Math.min(max, aligned)));
      syncQuantityButtons();
    };
    [quantityMinus, quantityPlus].forEach((button, index) => button?.addEventListener('click', () => {
      if (!quantityInput || quantityInput.disabled) return;
      normalizeQuantity();
      quantityInput.value = String(Number(quantityInput.value) + (index ? 1 : -1) * quantityBounds().step);
      normalizeQuantity();
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    quantityInput?.addEventListener('change', normalizeQuantity);
    quantityInput?.addEventListener('input', syncQuantityButtons);
    quantityInput?.addEventListener('blur', normalizeQuantity);
    const variantsScript = qs('[data-product-variants]', productRoot);
    const select = qs('[data-variant-select]', productRoot);
    const radios = qsa('[data-variant-radio]', productRoot);
    let variants = [];
    try { variants = JSON.parse(variantsScript?.textContent || '[]'); } catch (error) { variants = []; }
    const updateVariant = (variant, announce = false) => {
      if (!variant) return;
      const priceWrapper = qs('[data-price-wrapper]', productRoot);
      const currentPrice = qs('[data-product-price]', productRoot);
      const comparePrice = qs('[data-compare-price]', productRoot);
      const availability = qs('[data-product-availability]', productRoot);
      const addButton = qs('[data-add-to-cart]', productRoot);
      const sku = qs('[data-product-sku]', productRoot);
      if (currentPrice) currentPrice.textContent = variant.priceText;
      if (comparePrice) {
        comparePrice.textContent = variant.compareText;
        comparePrice.hidden = !variant.onSale;
      }
      if (availability) {
        availability.classList.toggle('is-unavailable', !variant.available);
        const label = qs('[data-availability-text]', availability);
        if (label) label.textContent = variant.available ? strings.available : strings.unavailable;
      }
      if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? strings.addToCart : strings.soldOut; }
      if (sku) sku.textContent = variant.sku || '—';
      const summary = qs('[data-selected-variant]', productRoot);
      if (summary) summary.textContent = variant.title;
      qsa('[data-product-option-index]', productRoot).forEach((node) => { node.textContent = variant.options[Number(node.dataset.productOptionIndex)] || ''; });
      radios.forEach((radio) => {
        radio.checked = String(radio.value) === String(variant.id);
        radio.closest('.product-variant-choice')?.classList.toggle('is-selected', radio.checked);
      });
      priceWrapper?.classList.toggle('price--sale', variant.onSale);
      const badge = qs('[data-product-discount]', productRoot);
      const saved = qs('[data-price-saving]', productRoot);
      const savings = qs('[data-product-savings]', productRoot);
      if (badge) { badge.hidden = !variant.onSale; badge.textContent = '-' + variant.discount + '%'; }
      if (saved) { saved.hidden = !variant.onSale; saved.textContent = strings.save + ' ' + variant.discount + '%'; }
      if (savings) { savings.hidden = !variant.onSale; savings.textContent = strings.youSave + ' ' + variant.savingsText; }
      if (quantityInput) {
        quantityInput.min = String(variant.quantityMin);
        quantityInput.step = String(variant.quantityIncrement);
        if (variant.quantityMax === null) quantityInput.removeAttribute('max');
        else quantityInput.max = String(variant.quantityMax);
        quantityInput.disabled = !variant.available;
        normalizeQuantity();
      }
      const quantityRules = qs('[data-quantity-rules]', productRoot);
      if (quantityRules) { quantityRules.textContent = variant.quantityText; quantityRules.hidden = !variant.available; }
      const checkout = qs('[data-dynamic-checkout]', productRoot);
      if (checkout) checkout.hidden = !variant.available;
      activateMedia(variant.featuredMediaId);
      if (announce) {
        const announcement = qs('[data-variant-announcement]', productRoot);
        if (announcement) announcement.textContent = [variant.title, variant.priceText, variant.available ? strings.available : strings.unavailable].join('. ');
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }
    };
    select?.addEventListener('change', () => updateVariant(variants.find((variant) => String(variant.id) === String(select.value)), true));
    radios.forEach((radio) => radio.addEventListener('change', () => {
      if (!radio.checked || !select) return;
      select.value = radio.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }));
    const initialId = select?.value || qs('input[name="id"]', productRoot)?.value;
    updateVariant(variants.find((variant) => String(variant.id) === String(initialId)));
    if (select && variants.length && radios.length) {
      const options = qs('[data-variant-options]', productRoot);
      const fallback = qs('[data-variant-select-wrapper]', productRoot);
      if (options) options.hidden = false;
      if (fallback) fallback.hidden = true;
    }
    window.addEventListener('pageshow', () => {
      const selectedId = select?.value || qs('input[name="id"]', productRoot)?.value;
      updateVariant(variants.find((variant) => String(variant.id) === String(selectedId)));
    });
  }

  qsa('[data-recommendations-url]').forEach(async (section) => {
    if (section.innerHTML.trim()) return;
    try {
      const response = await fetch(section.dataset.recommendationsUrl);
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = qs('[data-recommendations-url]', documentFragment);
      if (replacement?.innerHTML.trim()) { section.innerHTML = replacement.innerHTML; initializeWishlist(section); }
    } catch (error) {  }
  });
})();
